import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useShare } from '../../context/ShareContext';
import { useCommunity } from '../../context/CommunityContext';
import { usePWA } from '../../hooks/usePWA';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { trackNavClick, trackSharePlatform, trackPWAInstall, trackLogout } from '../../lib/analytics';

const Navbar = () => {
  const { user, logout, unreadCount } = useShare();
  const { unreadNotifications } = useCommunity();
  const location = useLocation();
  const { isInstallable, installPWA } = usePWA();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Start', path: '/' },
    { name: 'Privat', path: '/feed?tab=private' },
    { name: 'Mittagstisch', path: '/feed?tab=commercial' },
    { name: 'Karte', path: '/map' },
    { name: 'Prospekte', path: '/brochures' },
    { name: 'Anbieten', path: '/add' },
  ];

  const isActive = (path) => {
    if (path.includes('?')) return `${location.pathname}${location.search}` === path;
    return location.pathname === path;
  };

  const isProfileComplete = user?.name?.trim?.() && user?.address?.trim?.();
  const isAdminOrMod = ['admin', 'moderator'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const handleGlobalShare = async () => {
    const text = 'Rette mit mir Lebensmittel in der Schlei-Region. Mach mit bei SchleiShare:';
    const url = 'https://schleishare.de';
    trackSharePlatform({ source: 'navbar' });

    try {
      if (navigator.share) {
        await navigator.share({ title: 'SchleiShare', text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        alert('Text und Link wurden kopiert.');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') alert('Der Link konnte nicht geteilt werden.');
    }
  };

  const handleLogout = async () => {
    trackLogout();
    await logout();
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (target) => {
    trackNavClick({ target, location: 'navbar' });
    setIsMobileMenuOpen(false);
  };

  const handlePWAInstall = async () => {
    trackPWAInstall();
    await installPWA();
  };

  const NavItem = ({ link, mobile = false }) => (
    <Link
      to={link.path}
      onClick={() => handleNavClick(link.name)}
      className={mobile
        ? `flex items-center gap-2 rounded-xl px-3 py-3 text-base font-medium ${isActive(link.path) ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700 hover:bg-gray-50'}`
        : `inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium ${isActive(link.path) ? 'border-emerald-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}
    >
      {link.name}
    </Link>
  );

  const AdminDropdownLink = ({ to, icon, children, adminOnly = false }) => {
    if (adminOnly && !isAdmin) return null;

    return (
      <Link
        to={to}
        onClick={() => handleNavClick(children)}
        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive(to) ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50 hover:text-purple-700'}`}
      >
        <SafeIcon icon={icon} className="text-base" />
        {children}
      </Link>
    );
  };

  return (
    <nav className="relative sticky top-0 z-50 bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex min-w-0 items-center">
            <Link to="/" onClick={() => handleNavClick('Start')} className="mr-4 flex items-center gap-2 xl:mr-8">
              <SafeIcon icon={FiIcons.FiHeart} className="text-2xl text-emerald-600" />
              <span className="text-xl font-bold tracking-tight text-gray-900">Schlei<span className="text-emerald-600">Share</span></span>
            </Link>

            <div className="hidden items-stretch gap-5 xl:flex">
              {navLinks.map((link) => <NavItem key={link.path} link={link} />)}

              {isAdminOrMod && (
                <div className="group relative flex items-stretch">
                  <Link
                    to="/admin"
                    onClick={() => handleNavClick('Admin-Bereich')}
                    className={`inline-flex items-center gap-1.5 border-b-2 px-1 pt-1 text-sm font-bold ${isAdminRoute ? 'border-purple-600 text-purple-700' : 'border-transparent text-purple-600 hover:border-purple-300'}`}
                  >
                    <SafeIcon icon={FiIcons.FiShield} />
                    Admin
                    <SafeIcon icon={FiIcons.FiChevronDown} className="text-xs" />
                  </Link>

                  <div className="invisible absolute left-0 top-full z-[70] w-72 translate-y-1 pt-2 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                    <div className="rounded-2xl border border-gray-100 bg-white p-2 shadow-2xl">
                      <AdminDropdownLink to="/admin" icon={FiIcons.FiGrid}>Admin-Bereich</AdminDropdownLink>
                      <AdminDropdownLink to="/admin/menu-sources" icon={FiIcons.FiRefreshCw}>Wochenplan-Automatik</AdminDropdownLink>
                      <AdminDropdownLink to="/admin/users" icon={FiIcons.FiUsers} adminOnly>Nutzerprofile</AdminDropdownLink>
                      <AdminDropdownLink to="/admin/moderation" icon={FiIcons.FiFlag}>Moderation</AdminDropdownLink>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden items-center gap-2 xl:flex">
            <button type="button" onClick={handleGlobalShare} className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-sm font-bold text-blue-600 hover:bg-blue-100"><SafeIcon icon={FiIcons.FiShare2} /> Weitersagen</button>
            {isInstallable && <button type="button" onClick={handlePWAInstall} className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-600 hover:bg-emerald-100"><SafeIcon icon={FiIcons.FiDownload} /> App</button>}

            {user ? (
              <div className="ml-2 flex items-center gap-2 border-l border-gray-200 pl-3">
                <Link to="/saved" className="rounded-lg p-2 text-gray-500 hover:bg-rose-50 hover:text-rose-600" title="Merkliste"><SafeIcon icon={FiIcons.FiHeart} /></Link>
                <Link to="/notifications" className="relative rounded-lg p-2 text-gray-500 hover:bg-emerald-50 hover:text-emerald-600" title="Benachrichtigungen">
                  <SafeIcon icon={FiIcons.FiBell} />
                  {unreadNotifications > 0 && <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-red-500 px-1 text-center text-[9px] font-black text-white">{unreadNotifications}</span>}
                </Link>
                <Link to="/chat" className="relative rounded-lg p-2 text-gray-500 hover:bg-emerald-50 hover:text-emerald-600" title="Nachrichten">
                  <SafeIcon icon={FiIcons.FiMessageCircle} />
                  {unreadCount > 0 && <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-red-500 px-1 text-center text-[9px] font-black text-white">{unreadCount}</span>}
                </Link>
                <Link to="/profile" onClick={() => handleNavClick('Profil')} className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-emerald-600">
                  <div className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-emerald-200 bg-emerald-100 font-bold text-emerald-700">
                    {user.avatarUrl ? <img src={user.avatarUrl} alt="Avatar" className="h-full w-full object-cover" /> : user.name?.[0]?.toUpperCase() || '?'}
                    {isProfileComplete && <SafeIcon icon={FiIcons.FiCheckCircle} className="absolute -bottom-1 -right-1 rounded-full bg-white text-xs text-emerald-500" />}
                  </div>
                  <span className="hidden 2xl:inline">{user.name || 'Profil'}</span>
                </Link>
                <button type="button" onClick={handleLogout} className="px-2 py-2 text-sm text-gray-400 hover:text-red-500">Abmelden</button>
              </div>
            ) : (
              <Link to="/auth" className="ml-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Anmelden</Link>
            )}
          </div>

          <div className="flex items-center gap-2 xl:hidden">
            {user && (
              <Link to="/notifications" className="relative rounded-lg bg-emerald-50 p-2 text-emerald-700">
                <SafeIcon icon={FiIcons.FiBell} />
                {unreadNotifications > 0 && <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-red-500 px-1 text-center text-[9px] font-black text-white">{unreadNotifications}</span>}
              </Link>
            )}
            <button type="button" onClick={handleGlobalShare} className="rounded-lg bg-blue-50 p-2 text-blue-600"><SafeIcon icon={FiIcons.FiShare2} /></button>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((value) => !value)}
              className="rounded-lg p-2 text-2xl text-gray-500 hover:bg-gray-100"
              aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              <SafeIcon icon={isMobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-navigation"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.16 }}
            className="absolute inset-x-0 top-full max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-gray-200 bg-white shadow-xl xl:hidden"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-1 px-4 py-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {navLinks.map((link) => <NavItem key={link.path} link={link} mobile />)}

              {user && (
                <>
                  <div className="my-2 border-t border-gray-100" />
                  <Link to="/saved" onClick={() => handleNavClick('Merkliste')} className={`flex items-center gap-2 rounded-xl px-3 py-3 text-base font-medium ${isActive('/saved') ? 'bg-rose-50 text-rose-700' : 'text-gray-700'}`}><SafeIcon icon={FiIcons.FiHeart} /> Merkliste</Link>
                  <Link to="/notifications" onClick={() => handleNavClick('Benachrichtigungen')} className={`flex items-center justify-between rounded-xl px-3 py-3 text-base font-medium ${isActive('/notifications') ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'}`}>
                    <span className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiBell} /> Benachrichtigungen</span>
                    {unreadNotifications > 0 && <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-black text-white">{unreadNotifications}</span>}
                  </Link>
                  <Link to="/chat" onClick={() => handleNavClick('Nachrichten')} className={`flex items-center justify-between rounded-xl px-3 py-3 text-base font-medium ${isActive('/chat') ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'}`}>
                    <span className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiMessageCircle} /> Nachrichten</span>
                    {unreadCount > 0 && <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-black text-white">{unreadCount}</span>}
                  </Link>
                  <Link to="/profile" onClick={() => handleNavClick('Profil')} className={`flex items-center gap-2 rounded-xl px-3 py-3 text-base font-medium ${isActive('/profile') ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'}`}><SafeIcon icon={FiIcons.FiUser} /> Mein Profil</Link>
                </>
              )}

              {isAdminOrMod && (
                <>
                  <div className="my-2 border-t border-gray-100" />
                  <p className="px-3 pb-1 pt-2 text-xs font-black uppercase tracking-wider text-gray-400">Verwaltung</p>
                  <Link to="/admin" onClick={() => handleNavClick('Admin-Bereich')} className={`flex items-center gap-2 rounded-xl px-3 py-3 text-base font-bold ${isActive('/admin') ? 'bg-purple-50 text-purple-700' : 'text-purple-600 hover:bg-purple-50'}`}><SafeIcon icon={FiIcons.FiGrid} /> Admin-Bereich</Link>
                  <Link to="/admin/menu-sources" onClick={() => handleNavClick('Wochenplan-Automatik')} className={`flex items-center gap-2 rounded-xl px-3 py-3 text-base font-bold ${isActive('/admin/menu-sources') ? 'bg-blue-50 text-blue-700' : 'text-blue-600 hover:bg-blue-50'}`}><SafeIcon icon={FiIcons.FiRefreshCw} /> Wochenplan-Automatik</Link>
                  {isAdmin && <Link to="/admin/users" onClick={() => handleNavClick('Nutzerprofile')} className={`flex items-center gap-2 rounded-xl px-3 py-3 text-base font-bold ${isActive('/admin/users') ? 'bg-purple-50 text-purple-700' : 'text-purple-600 hover:bg-purple-50'}`}><SafeIcon icon={FiIcons.FiUsers} /> Nutzerprofile</Link>}
                  <Link to="/admin/moderation" onClick={() => handleNavClick('Moderation')} className={`flex items-center gap-2 rounded-xl px-3 py-3 text-base font-bold ${isActive('/admin/moderation') ? 'bg-purple-50 text-purple-700' : 'text-purple-600 hover:bg-purple-50'}`}><SafeIcon icon={FiIcons.FiFlag} /> Moderation</Link>
                </>
              )}

              <div className="my-2 border-t border-gray-100" />
              {isInstallable && <button type="button" onClick={handlePWAInstall} className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-50 px-3 py-3 font-bold text-emerald-700"><SafeIcon icon={FiIcons.FiDownload} /> App installieren</button>}
              {!user ? <Link to="/auth" onClick={() => handleNavClick('Anmelden')} className="block rounded-xl bg-emerald-600 px-3 py-3 text-center font-bold text-white">Anmelden / Registrieren</Link> : <button type="button" onClick={handleLogout} className="block w-full rounded-xl bg-red-50 px-3 py-3 text-center font-bold text-red-600">Abmelden</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
